import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { OverlayPanel } from "primereact/overlaypanel";

import AcessibilityIcon from "@/assets/icons/acessibility.svg?react";
import { Icon } from "@/ds";
import { UserAvatar } from "@/shared/components/user-avatar";

import { NAVIGATOR_USER_EDIT_HINT, NAVIGATOR_USER_PANEL_LABEL } from "./navigator-user.constants";
import styles from "./navigator-user.module.scss";
import type { NavigatorUserViewModel } from "./types-navigator-user";

/**
 * Usuário do Navigator: o gatilho (avatar, nome, área e chevron; ou só o avatar) e o `user-profile-modal` com e-mail,
 * cargo, aviso de edição na Microsoft, Acessibilidade e Sair (Figma 5037-105413 e 5037-105894).
 */
export function NavigatorUserView(vm: NavigatorUserViewModel) {
  return (
    <>
      <button
        type="button"
        className={styles.userTrigger}
        data-variant={vm.variant}
        onClick={vm.togglePanel}
        aria-label={vm.triggerLabel}
        aria-haspopup="dialog"
        aria-expanded={vm.panelOpen}
        data-testid={vm.testIds.button}
      >
        <UserAvatar nome={vm.name} fotoSrc={vm.photoSrc} />

        {vm.showDetails ? (
          <span className={styles.userInfo}>
            <span className={styles.userNameRow}>
              <span className={styles.userName}>{vm.displayName}</span>
              <Icon name="chevron-up" size={10.5} className={styles.userChevron} />
            </span>
            {vm.area ? <span className={styles.userArea}>{vm.area}</span> : null}
          </span>
        ) : null}
      </button>

      <OverlayPanel
        ref={vm.panelRef}
        className="p-overlaypanel-profile"
        dismissable
        showCloseIcon={false}
        onShow={vm.onPanelShow}
        onHide={vm.onPanelHide}
      >
        <div
          className={styles.profileBody}
          data-testid={vm.testIds.panel}
          role="dialog"
          aria-label={NAVIGATOR_USER_PANEL_LABEL}
        >
          <div className={styles.profileHeader}>
            <UserAvatar nome={vm.name} fotoSrc={vm.photoSrc} />
            <span className={styles.profileIdentity}>
              <span className={styles.profileName}>{vm.displayName}</span>
              {vm.area ? <span className={styles.userArea}>{vm.area}</span> : null}
            </span>
          </div>

          <dl className={styles.profileInfo}>
            {vm.email ? (
              <div className={styles.profileInfoRow}>
                <dt className={styles.profileInfoTerm}>
                  <Icon name="envelope" size={16} />
                  <span className={styles.srOnly}>E-mail</span>
                </dt>
                <dd className={styles.profileInfoValue}>{vm.email}</dd>
              </div>
            ) : null}

            {vm.jobTitle ? (
              <div className={styles.profileInfoRow}>
                <dt className={styles.profileInfoTerm}>
                  <Icon name="briefcase" size={16} />
                  <span className={styles.srOnly}>Cargo</span>
                </dt>
                <dd className={styles.profileInfoValue}>{vm.jobTitle}</dd>
              </div>
            ) : null}
          </dl>

          <hr className={styles.divider} aria-hidden />

          <Message
            severity="secondary"
            className="p-message-xs p-message-block"
            icon={<Icon name="info-circle" size={16} className="p-inline-message-icon" />}
            text={NAVIGATOR_USER_EDIT_HINT}
          />

          <hr className={styles.divider} aria-hidden />

          <div className={styles.profileActions}>
            <Button
              type="button"
              className={styles.profileAction}
              label="Acessibilidade"
              icon={
                <AcessibilityIcon className="p-button-icon p-button-icon-left" aria-hidden="true" />
              }
              severity="secondary"
              size="small"
              outlined
              data-testid={vm.testIds.accessibility}
            />
            <Button
              type="button"
              className={styles.profileAction}
              label="Sair"
              icon="pi pi-sign-out"
              severity="danger"
              size="small"
              outlined
              onClick={vm.logout}
              loading={vm.isLoggingOut}
              data-testid={vm.testIds.logout}
            />
          </div>
        </div>
      </OverlayPanel>
    </>
  );
}
